import React, { useState } from 'react';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import Icon from 'components/Utils/NewIcon';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import type { PharmacyDirectory } from 'generated/api';

import './style.scss';

interface PharmacyFaxEditorProps {
  pharmacy: PharmacyDirectory;
  /** Called with the updated directory row after a successful save. */
  onUpdated?: (pharmacy: PharmacyDirectory) => void;
}

/**
 * Compact chip + inline editor for a pharmacy's manually-curated `preferred_fax`
 * override, designed to sit inside a row of the pharmacy search list.
 *
 * Faxes are sent to `preferred_fax` when set, otherwise the OCP-sourced `fax`
 * (e.g. PocketPills lists one official fax with the OCP but wants prescriptions
 * sent elsewhere). The override lives on the global directory row, survives the
 * nightly OCP sync, and applies everywhere the pharmacy is used.
 *
 * Every interaction calls stopPropagation so editing the fax never selects the
 * pharmacy or closes the modal.
 */
const PharmacyFaxEditor = ({ pharmacy, onUpdated }: PharmacyFaxEditorProps) => {
  const dispatch = useAppDispatch();
  // Reflect a freshly-saved override immediately, independent of the redux list
  // (which is keyed off the last search and may not be refetched).
  const [current, setCurrent] = useState<PharmacyDirectory>(pharmacy);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(pharmacy.preferred_fax || '');

  const hasOverride = !!current.preferred_fax;

  function stop(e?: React.SyntheticEvent) {
    e?.stopPropagation?.();
  }

  function open(e?: React.MouseEvent) {
    stop(e);
    setValue(current.preferred_fax || '');
    setEditing(true);
  }

  async function save(e?: React.MouseEvent) {
    stop(e);
    if (!current.id) return;
    const next = value.trim();
    setSaving(true);
    const result = await dispatch(
      api.Pharmacies.setPharmacyPreferredFax({
        id: current.id,
        pharmacyPreferredFaxRequest: { preferred_fax: next },
      }),
      {
        // `title` drives both the success and error toasts in the dispatch wrapper.
        title: 'Preferred Fax',
        successText: next
          ? 'Saved preferred fax for this pharmacy'
          : 'Cleared the preferred fax for this pharmacy',
        errorText: 'Could not update the preferred fax',
      },
    );
    setSaving(false);
    if (result) {
      setCurrent(result);
      setEditing(false);
      onUpdated?.(result);
    }
  }

  function cancel(e?: React.SyntheticEvent) {
    stop(e);
    setEditing(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    // Keep keystrokes inside the editor — Enter/Escape must not reach the row.
    e.stopPropagation();
    if (e.key === 'Enter' && !saving) {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  }

  if (editing) {
    return (
      <div className="pharmacy-fax-editor editing" onClick={stop}>
        <div className="pharmacy-fax-editor-title">
          <Icon src="fax" width="13px" />
          <span>Preferred fax for {current.name || 'this pharmacy'}</span>
        </div>
        <DefaultTextField
          autoFocus
          label="Preferred fax"
          placeholder={current.fax || 'Fax number'}
          value={value}
          onClick={stop}
          onKeyDown={onKeyDown}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
        />
        <span className="hint">
          Where prescriptions are actually faxed. Leave blank to fall back to the listed fax
          {current.fax ? ` (${current.fax})` : ''}.
        </span>
        <div className="pharmacy-fax-editor-actions">
          <DefaultButton
            // @ts-ignore type is forwarded to the underlying button
            type="button"
            name="Save"
            loading={saving}
            onClick={save}
          />
          <DefaultButton
            // @ts-ignore type is forwarded to the underlying button
            type="button"
            name="Cancel"
            className="white"
            disabled={saving}
            onClick={cancel}
          />
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`pharmacy-fax-trigger ${hasOverride ? 'has-override' : ''}`}
      title={
        hasOverride
          ? `Prescriptions fax to ${current.preferred_fax} — click to edit the preferred fax`
          : 'Set a preferred fax for this pharmacy'
      }
      aria-label={hasOverride ? 'Edit preferred fax' : 'Set preferred fax'}
      onClick={open}
    >
      <Icon src={hasOverride ? 'fax' : 'pen'} width="12px" />
    </button>
  );
};

export default PharmacyFaxEditor;
